import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { BehaviorSubject, Observable, Subject, debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs';

@Component({
  selector: 'tlp-generic-select',
  templateUrl: './generic-select.component.html',
  styleUrls: ['./generic-select.component.css']
})
export class GenericSelectComponent implements OnInit, OnDestroy, OnChanges {
@Input() items: any[] = [];
@Input() multiple = false;
@Input() initialSelectedItems: any | any[] = [];
@Input() set maxSelectedItems(value: number | null) {
    this._maxSelectedItems = value ?? Infinity;
  }
  get maxSelectedItems(): number {
    return this._maxSelectedItems;
  }
  private _maxSelectedItems: number = Infinity;
  /**
   * The property name to display in the dropdown. This should match a property name in your items array.
   * For example, if your items look like [{text: 'Item 1'}, {text: 'Item 2'}], you would set bindLabel="text"
   * @default 'displayName'
   */
  @Input() bindLabel = 'displayName';
  @Input() placeholder = 'Search items';
  @Output() selectionChange = new EventEmitter<any>();

  searchForm: FormGroup;
  allItems: any[] = [];
  selectedItems: any | any[] = [];
  
  searchInput$ = new Subject<string>();
  isLoading$ = new BehaviorSubject<boolean>(false);
  isPreselecting$ = new BehaviorSubject<boolean>(false);
  hasClicked$ = new BehaviorSubject<boolean>(false);
  errorMessage$ = new BehaviorSubject<string | null>(null);
  
  private destroy$ = new Subject<void>();

  constructor(private fb: FormBuilder) {
    this.searchForm = this.fb.group({
      search: ['']
    });

    // Initialize observables
    this.searchInput$.pipe(
      takeUntil(this.destroy$),
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(term => {
      this.handleSearch(term);
    });
  }

  ngOnInit() {
    this.allItems = this.items;
    if (this.initialSelectedItems) {
      this.selectedItems = this.initialSelectedItems;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['items']) {
      this.allItems = changes['items'].currentValue;
    }
    if (changes['initialSelectedItems'] && !changes['initialSelectedItems'].firstChange) {
      this.selectedItems = changes['initialSelectedItems'].currentValue;
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSelectionChange(event: any) {
    this.selectionChange.emit(event);
  }

  onSelectionOpen(event: any) {
    this.hasClicked$.next(true);
  }

  onNgSelectClick() {
    this.hasClicked$.next(true);
  }

  private handleSearch(term: string) {
    try {
      this.isLoading$.next(true);
      const filteredItems = this.filterItems(term);
      this.allItems = filteredItems;
      this.errorMessage$.next(null);
    } catch (error) {
      this.errorMessage$.next('An error occurred while searching');
    } finally {
      this.isLoading$.next(false);
    }
  }

  private filterItems(term: string): any[] {
    if (!term) {
      return this.items;
    }
    return this.items.filter(item => 
      item[this.bindLabel].toLowerCase().includes(term.toLowerCase())
    );
  }
}
